import { cn } from "@/lib/utils"
import React, { forwardRef } from "react"

export const H1 = forwardRef<
	HTMLHeadingElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h1
		ref={ref}
		className={cn(
			"scroll-m-20 text-4xl font-extrabold tracking-[-0.576px] lg:text-5xl font",
			className
		)}
		{...props}
	/>
))
H1.displayName = "H1"

export const H2 = forwardRef<
	HTMLHeadingElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h2
		ref={ref}
		className={cn(
			"scroll-m-20 text-3xl font-semibold tracking-[-0.225px] first:mt-0",
			className
		)}
		{...props}
	/>
))
H2.displayName = "H2"

export const H3 = forwardRef<
	HTMLHeadingElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h3
		ref={ref}
		className={cn(
			"scroll-m-20 text-2xl font-semibold tracking-[-0.144px]",
			className
		)}
		{...props}
	/>
))
H3.displayName = "H3"

export const H4 = forwardRef<
	HTMLHeadingElement,
	React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
	<h4
		ref={ref}
		className={cn("scroll-m-20 text-xl font-semibold tracking-[-0.1px]", className)}
		{...props}
	/>
))
H4.displayName = "H4"

export const Lead = forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p ref={ref} className={cn("text-xl text-muted-foreground", className)} {...props} />
))
Lead.displayName = "Lead"

export const P = forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p ref={ref} className={cn("leading-7", className)} {...props} />
))
P.displayName = "P"

export const Large = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => (
		<div ref={ref} className={cn("text-lg font-semibold", className)} {...props} />
	)
)
Large.displayName = "Large"

export const Small = forwardRef<
	HTMLParagraphElement,
	React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
	<p
		ref={ref}
		className={cn("text-sm font-medium leading-5", className)}
		{...props}
	/>
))
Small.displayName = "Small"

export const Muted = forwardRef<HTMLSpanElement, React.HTMLAttributes<HTMLSpanElement>>(
	({ className, ...props }, ref) => (
		<span
			ref={ref}
			className={cn("text-sm text-muted-foreground", className)}
			{...props}
		/>
	)
)
Muted.displayName = "Muted"

export const InlineCode = forwardRef<
	HTMLSpanElement,
	React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
	<code
		ref={ref}
		className={cn(
			"relative rounded bg-muted px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
			className
		)}
		{...props}
	/>
))
InlineCode.displayName = "InlineCode"

export const MultilineCode = forwardRef<
	HTMLPreElement,
	React.HTMLAttributes<HTMLPreElement>
>(({ className, ...props }, ref) => (
	<pre
		ref={ref}
		className={cn(
			"relative rounded bg-muted p-4 font-mono text-sm font-semibold overflow-x-auto",
			className
		)}
		{...props}
	/>
))
MultilineCode.displayName = "MultilineCode"

export const Blockquote = forwardRef<
	HTMLQuoteElement,
	React.HTMLAttributes<HTMLQuoteElement>
>(({ className, ...props }, ref) => (
	<blockquote
		ref={ref}
		className={cn("mt-6 border-l-2 pl-6 italic", className)}
		{...props}
	/>
))
Blockquote.displayName = "Blockquote"

export const Body = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => (
		<div
			ref={ref}
			className={cn("text-sm leading-6 font-normal text-foreground", className)}
			{...props}
		/>
	)
)
export const BodyMedium = forwardRef<
	HTMLDivElement,
	React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
	<Body ref={ref} className={cn("font-medium", className)} {...props} />
))
